import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "@phosphor-icons/react";
import { initConsent, setConsent, getConsent, hasGPC, OPEN_SETTINGS_EVENT } from "@/lib/consent";

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    const rec = initConsent();
    setCurrent(rec);
    if (!rec) setOpen(true);
    const onOpen = () => {
      setCurrent(getConsent());
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
  }, []);

  const decide = (ads) => {
    setCurrent(setConsent(ads));
    setOpen(false);
  };

  const gpc = hasGPC();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie preferences"
          data-testid="cookie-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[70] left-4 right-4 bottom-20 md:bottom-6 md:left-6 md:right-auto md:max-w-md rounded-2xl bg-[#0e0e10] border border-white/10 p-5 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-brand-700/30 text-brand-400 flex-shrink-0">
              <Cookie size={18} weight="regular" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-white text-[15px] font-medium">Cookie preferences</div>
              <p className="mt-1.5 text-[13px] text-zinc-300 leading-relaxed">
                The site works without tracking cookies. The only optional one is Google Ads conversion measurement,
                which tells us whether an ad led to an inquiry. It stays off unless you accept.
              </p>
              {gpc && (
                <p data-testid="cookie-gpc-note" className="mt-2 text-[12.5px] text-emerald-300/90">
                  Your browser sent a Global Privacy Control signal, so advertising cookies are off by default.
                </p>
              )}
              {current && (
                <p data-testid="cookie-current" className="mt-2 text-[12.5px] text-zinc-400">
                  Current choice: {current.ads ? "advertising cookies allowed" : "advertising cookies declined"}.
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => decide(true)}
                  data-testid="cookie-accept"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
                >
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => decide(false)}
                  data-testid="cookie-decline"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-white/15 text-white text-[13.5px] hover:bg-white/[0.06] transition-colors"
                >
                  Decline
                </button>
                <Link
                  to="/cookie-policy"
                  data-testid="cookie-policy-link"
                  className="ml-auto text-[12.5px] text-zinc-400 underline underline-offset-4 hover:text-white transition-colors"
                >
                  Cookie Policy
                </Link>
              </div>
            </div>
            {current && (
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close cookie preferences"
                data-testid="cookie-close"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors -mr-1 -mt-1"
              >
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
